// ─────────────────────────────────────────────
//  slug — the stable id every Team-directory person is known by, and the name
//  splitting that feeds it.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  The directory is stored as ONE Firestore document that the weekly sync
//  replaces wholesale (TEAM-CONTRACT §3). That only works because each person
//  carries an id derived from their name alone: the page can key a list, a
//  favourite or a deep link on it and the sync can overwrite everything
//  underneath without the page remembering a thing.
//
//  ⚠️ THE SLUG MUST BE STABLE ACROSS SYNCS. It is a pure function of
//  (sheetRole, firstName, lastName). Never fold in a row number, a timestamp,
//  a phone number or an array index — the sheet is re-sorted by hand and any of
//  those would re-key the whole directory on a week when nothing changed.
//
//  ⚠️ The two tabs write names two different ways: the GFA tab splits Last and
//  First into their own columns, the SRNA and surgeon blocks pack the whole
//  name into ONE cell. `splitName` owns the one-cell case (TEAM-CONTRACT §2.1):
//  first comma wins, otherwise the LAST space. Do not "improve" it with a
//  particle list ("de la", "van") — "Feliz de la Cruz, Victor" already arrives
//  with its comma, and guessing would move ids for people who are fine today.
//
//  Pure — no Firebase, no React, no network.
// ─────────────────────────────────────────────

/**
 * Lowercase, de-accent, and hyphenate a name fragment for use inside an id.
 * Any run of non-alphanumerics becomes one "-"; leading/trailing "-" are cut.
 *
 * @param {*} s
 * @returns {string}
 */
export function slugify(s) {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Drop a parenthetical note and collapse whitespace, keeping the real name.
 * "Babajide(Michael)" → "Babajide"; "Rinaldo (EP) " → "Rinaldo".
 *
 * ⚠️ If the parenthetical was the ENTIRE cell the original is returned instead
 * of "" — a cell that reads "(EP)" is a person we cannot name, not a person we
 * are allowed to delete.
 *
 * @param {*} s
 * @returns {string}
 */
export function cleanNamePart(s) {
  const raw = String(s ?? "").trim();
  const stripped = raw.replace(/\([^)]*\)?/g, " ").replace(/\s+/g, " ").trim();
  return stripped || raw;
}

/**
 * Split a ONE-CELL name into parts (TEAM-CONTRACT §2.1).
 *   "Abbey, Patrick"           → { firstName: "Patrick", lastName: "Abbey" }
 *   "Katherine Bacon"          → { firstName: "Katherine", lastName: "Bacon" }
 *   "Alexander, Gerald (Trey)" → { …, annotated: true }
 *   "Arturo"                  → { firstName: "", lastName: "Arturo" }
 *
 * A parenthetical is stripped BEFORE splitting, so it can never be mistaken for
 * the surname ("Rinaldo (EP)" would otherwise split on the space into "(EP)").
 *
 * @param {*} cell
 * @returns {{ firstName: string, lastName: string, annotated: boolean }}
 */
export function splitName(cell) {
  const raw = String(cell ?? "").trim();
  if (!raw) return { firstName: "", lastName: "", annotated: false };

  const annotated = /[()]/.test(raw);
  const clean = cleanNamePart(raw);

  const comma = clean.indexOf(",");
  if (comma >= 0) {
    return {
      firstName: clean.slice(comma + 1).trim(),
      lastName: clean.slice(0, comma).trim(),
      annotated,
    };
  }

  const space = clean.lastIndexOf(" ");
  if (space < 0) return { firstName: "", lastName: clean, annotated };
  return {
    firstName: clean.slice(0, space).trim(),
    lastName: clean.slice(space + 1).trim(),
    annotated,
  };
}

/**
 * `${sheetRole}:${slug(lastName)}-${slug(firstName)}` (TEAM-CONTRACT §2.1),
 * with an empty half omitted so a one-name person gets "tech:arturo" rather
 * than a dangling "tech:arturo-".
 *
 * @param {string} sheetRole
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string}
 */
export function personSlug(sheetRole, firstName, lastName) {
  const tail = [slugify(lastName), slugify(firstName)].filter(Boolean).join("-");
  return `${sheetRole}:${tail}`;
}

/**
 * Resolve id collisions across an ALREADY-ORDERED list (TEAM-CONTRACT §2.1).
 * The first person to claim a slug keeps it; later ones get "-2", "-3" … in
 * source-row order and the `duplicate-slug` flag.
 *
 * ⚠️ Order in = order out, and the input is never mutated. The suffix is
 * assigned by POSITION, so the caller must pass people in a deterministic
 * order (buildDirectory passes GFA-then-surgeons, each in sheet-row order)
 * or two people would swap ids between syncs.
 *
 * ⚠️ Only the LATER colliders are flagged. The first record is the one every
 * existing reference already points at; flagging it would light up a warning on
 * a person whose id never changed.
 *
 * @param {Array<{id: string, flags?: string[]}>} people
 * @returns {Array<object>} new array of new objects
 */
export function dedupeSlugs(people) {
  const list = Array.isArray(people) ? people : [];
  const taken = new Set();
  const nextSuffix = new Map();

  return list.map((person) => {
    const flags = Array.isArray(person?.flags) ? [...person.flags] : [];
    const base = String(person?.id ?? "");

    if (!taken.has(base)) {
      taken.add(base);
      return { ...person, flags };
    }

    // "-2" before "-3"; skip any suffix a real person already owns.
    let n = nextSuffix.get(base) || 1;
    let candidate;
    do {
      n += 1;
      candidate = `${base}-${n}`;
    } while (taken.has(candidate));
    nextSuffix.set(base, n);
    taken.add(candidate);

    if (!flags.includes("duplicate-slug")) flags.push("duplicate-slug");
    return { ...person, id: candidate, flags };
  });
}
